/**
 * MKS-410 — triage a REQUEST without leaving the queue.
 *
 * Four decisions, deliberately four controls rather than one Save:
 *   - route to a PERSON   (PATCH .../assignee)
 *   - route to a PROJECT  (POST  .../route-to-project — creates the ticket in
 *                          that project's own Triage column)
 *   - start work          (POST  .../status in_progress)
 *   - close it            (POST  .../status resolved | rejected)
 * Triage is independent decisions, and making the user press Save after each
 * one — or losing two of them when the third fails — is worse than N round
 * trips. Same reasoning as MKS-339's `TriagePanel`.
 *
 * NOTHING IS DISABLED THAT THE BACKEND CAN EXPLAIN BETTER. `TriagePanel`'s
 * standing rule: a greyed-out control says only "no", while the server says
 * WHICH prerequisite is missing. So "Route to project" stays pressable on a
 * project with no Triage column, and the 422
 * `helpdesk_project_has_no_triage_column` is surfaced as a sentence. The only
 * things disabled here are controls with genuinely nothing behind them (a
 * closed request) and the one in flight.
 *
 * MARKING UNROUTABLE PROJECTS. There is no endpoint that says which projects
 * have a Triage column — `resolveTriageStateId` runs server-side inside the
 * routing call. So the picker learns: a project the backend has refused for
 * that reason is remembered for the rest of the session and reads
 * "— no Triage column" from then on. It stays SELECTABLE, per the rule above;
 * the marking is a hint, and the refusal remains the authority.
 */
import { useState } from 'react';
import { Btn } from '../ui/Btn';
import { DropdownSelect } from '../ui/DropdownSelect';
import { useToast } from '../ui/Toast';
import { useProjects } from '../../hooks/useProjects';
import { useRequestCategories } from '../../hooks/useHelpdesk';
import {
  useRouteRequestToPerson,
  useRouteRequestToProject,
  useSetRequestStatus,
} from '../../hooks/useHelpdeskRequests';
import { requestPriorityLabel } from './requestFormat';
import {
  HELPDESK_REQUEST_PRIORITIES,
  MAX_REJECTION_REASON_LENGTH,
  MAX_RESOLUTION_NOTE_LENGTH,
  type HelpdeskRequestRecord,
} from '../../services/api/helpdeskRequests';
import type { AdminUser } from '../../services/api/admin';

export interface RequestTriagePanelProps {
  request: HelpdeskRequestRecord;
  /** Who a request may be routed to. Fetched once by the page, not per row. */
  people: AdminUser[];
  /**
   * Projects the backend has refused with
   * `helpdesk_project_has_no_triage_column` in this session, and a way to add
   * to that set. Lifted to the page so one row's refusal marks the picker on
   * every other row too.
   */
  unroutableProjectIds: ReadonlySet<number>;
  onProjectUnroutable: (projectId: number) => void;
}

/** The refusals this panel can say something better about than the raw message. */
function triageErrorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  switch (code) {
    case 'helpdesk_project_has_no_triage_column':
      return 'That project has no Triage column, so there is nowhere for the ticket to land. Add a Triage column to the project, or route this to a person instead.';
    case 'helpdesk_request_already_routed':
      return 'This request is already on a project. A request is routed once.';
    case 'helpdesk_request_closed':
      return 'This request is closed. Resolved and rejected are final.';
    case 'helpdesk_request_invalid_transition':
      return 'That is not a move this request can make from where it is.';
    case 'helpdesk_resolution_note_required':
      return 'Resolving needs a resolution note — it is the ISO record.';
    case 'helpdesk_rejection_reason_required':
      return 'Rejecting needs a reason — it is the ISO record.';
    default:
      return (err as Error)?.message ?? 'Could not save. Try again.';
  }
}

function personName(user: AdminUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name.length > 0 ? name : user.email;
}

export function RequestTriagePanel({
  request,
  people,
  unroutableProjectIds,
  onProjectUnroutable,
}: RequestTriagePanelProps) {
  const showToast = useToast();
  const [pending, setPending] = useState<string | null>(null);

  // Routing form state. Local because routing is one decision made of three
  // fields, unlike the assignee select which commits on change.
  const [projectId, setProjectId] = useState('');
  const [priority, setPriority] = useState(request.priority);
  const [categoryId, setCategoryId] = useState('');

  // Closing form state.
  const [closing, setClosing] = useState<'resolved' | 'rejected' | null>(null);
  const [closingText, setClosingText] = useState('');
  const [closingError, setClosingError] = useState<string | null>(null);

  const { data: projects = [] } = useProjects();
  // Categories belong to the project the request is being routed TO; before
  // one is picked there is nothing to offer.
  const selectedProjectId = projectId === '' ? undefined : Number(projectId);
  const { data: categories = [] } = useRequestCategories(selectedProjectId);

  const routeToPerson = useRouteRequestToPerson();
  const routeToProject = useRouteRequestToProject();
  const setStatus = useSetRequestStatus();

  const closed = request.status === 'resolved' || request.status === 'rejected';

  function run(label: string, promise: Promise<unknown>, successMessage: string) {
    setPending(label);
    return promise
      .then(() => {
        showToast(successMessage, 'success');
        return true;
      })
      .catch((err) => {
        showToast(triageErrorMessage(err), 'error');
        return err;
      })
      .finally(() => setPending(null));
  }

  function handleRouteToProject() {
    if (selectedProjectId === undefined) {
      showToast('Pick a project first.', 'error');
      return;
    }
    const target = selectedProjectId;
    void run(
      'project',
      routeToProject.mutateAsync({
        requestId: request.id,
        input: {
          projectId: target,
          priority,
          categoryId: categoryId === '' ? null : Number(categoryId),
        },
      }),
      'Routed to project',
    ).then((result) => {
      // Remember a project the backend refused for want of a Triage column, so
      // the picker can mark it from here on. Any other refusal says nothing
      // durable about the project.
      if (
        result !== true &&
        (result as { code?: string } | null)?.code ===
          'helpdesk_project_has_no_triage_column'
      ) {
        onProjectUnroutable(target);
      }
    });
  }

  function handleClose() {
    if (!closing) return;
    const text = closingText.trim();
    if (text.length === 0) {
      setClosingError(
        closing === 'resolved'
          ? 'A resolution note is required.'
          : 'A reason for rejection is required.',
      );
      return;
    }
    setClosingError(null);
    void run(
      'close',
      setStatus.mutateAsync({
        requestId: request.id,
        input:
          closing === 'resolved'
            ? { status: 'resolved', resolutionNote: text }
            : { status: 'rejected', rejectionReason: text },
      }),
      closing === 'resolved' ? 'Request resolved' : 'Request rejected',
    ).then((result) => {
      if (result === true) {
        setClosing(null);
        setClosingText('');
      }
    });
  }

  return (
    <div
      className="space-y-4 px-4 py-3 bg-ms-surface-2 border-t border-ms-border"
      data-testid={`request-triage-panel-${request.id}`}
    >
      {/* The request's own words — triage without reading them is guessing. */}
      <p className="text-[12.5px] text-ms-text-secondary whitespace-pre-wrap max-w-3xl">
        {request.description}
      </p>

      {closed ? (
        <p className="text-[12.5px] text-ms-text-muted">
          This request is {request.status === 'resolved' ? 'resolved' : 'rejected'} and
          final.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            {/* Route to a person — commits on change, like MKS-339's Owner. */}
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] uppercase tracking-wider text-ms-text-muted">
                Handled by
              </span>
              <DropdownSelect
                aria-label="Handled by"
                value={request.assignee?.id ?? ''}
                disabled={pending !== null}
                onChange={(e) =>
                  void run(
                    'person',
                    routeToPerson.mutateAsync({
                      requestId: request.id,
                      assigneeId: e.target.value === '' ? null : Number(e.target.value),
                    }),
                    'Handler updated',
                  )
                }
              >
                <option value="">Unassigned</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {personName(p)}
                  </option>
                ))}
              </DropdownSelect>
            </label>

            {request.status !== 'in_progress' && (
              <Btn
                variant="secondary"
                size="md"
                disabled={pending !== null}
                onClick={() =>
                  void run(
                    'start',
                    setStatus.mutateAsync({
                      requestId: request.id,
                      input: { status: 'in_progress' },
                    }),
                    'Marked in progress',
                  )
                }
              >
                Start work
              </Btn>
            )}
          </div>

          {/* Route to a project. One decision, three fields, one button. */}
          {request.projectId == null ? (
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10.5px] uppercase tracking-wider text-ms-text-muted">
                  Route to project
                </span>
                <DropdownSelect
                  aria-label="Route to project"
                  value={projectId}
                  disabled={pending !== null}
                  onChange={(e) => {
                    setProjectId(e.target.value);
                    // The category list is the new project's; keeping the old
                    // selection would send a category from another project.
                    setCategoryId('');
                  }}
                >
                  <option value="">Pick a project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {unroutableProjectIds.has(p.id)
                        ? `${p.name} — no Triage column`
                        : p.name}
                    </option>
                  ))}
                </DropdownSelect>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[10.5px] uppercase tracking-wider text-ms-text-muted">
                  Priority
                </span>
                <DropdownSelect
                  aria-label="Priority"
                  value={priority}
                  disabled={pending !== null}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {HELPDESK_REQUEST_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {requestPriorityLabel(p)}
                    </option>
                  ))}
                </DropdownSelect>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[10.5px] uppercase tracking-wider text-ms-text-muted">
                  Category
                </span>
                <DropdownSelect
                  aria-label="Category"
                  value={categoryId}
                  // A project with no categories has nothing to pick, and the
                  // field is optional either way.
                  disabled={pending !== null || categories.length === 0}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">
                    {categories.length === 0 ? 'No categories' : 'No category'}
                  </option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </DropdownSelect>
              </label>

              {/* Deliberately NOT disabled when the project has no Triage
                  column — the 422 names the problem, a grey button does not. */}
              <Btn
                variant="accent"
                size="md"
                disabled={pending !== null}
                onClick={handleRouteToProject}
              >
                Route to project
              </Btn>
            </div>
          ) : (
            <p className="text-[12.5px] text-ms-text-secondary">
              Routed to <span className="font-semibold">{request.projectName}</span>
              {request.linkedTaskId != null && (
                <>
                  {' — '}
                  <a
                    className="text-primary-700 hover:underline"
                    href={`/projects/${request.projectId}/board?openTask=${request.linkedTaskId}`}
                  >
                    open the ticket
                  </a>
                </>
              )}
            </p>
          )}

          {/* Closing. The note and the reason are required server-side with
              locked codes, because that text IS the ISO record — the check
              here only saves a round trip. */}
          <div className="space-y-2">
            {closing === null ? (
              <div className="flex items-center gap-2">
                <Btn
                  variant="secondary"
                  size="sm"
                  disabled={pending !== null}
                  onClick={() => {
                    setClosing('resolved');
                    setClosingText('');
                    setClosingError(null);
                  }}
                >
                  Resolve…
                </Btn>
                <Btn
                  variant="ghost"
                  size="sm"
                  disabled={pending !== null}
                  onClick={() => {
                    setClosing('rejected');
                    setClosingText('');
                    setClosingError(null);
                  }}
                >
                  Reject…
                </Btn>
              </div>
            ) : (
              <div className="space-y-2 max-w-2xl">
                <label
                  className="block text-[10.5px] uppercase tracking-wider text-ms-text-muted"
                  htmlFor={`closing-text-${request.id}`}
                >
                  {closing === 'resolved' ? 'Resolution note' : 'Reason for rejection'}
                </label>
                <textarea
                  id={`closing-text-${request.id}`}
                  rows={3}
                  value={closingText}
                  maxLength={
                    closing === 'resolved'
                      ? MAX_RESOLUTION_NOTE_LENGTH
                      : MAX_REJECTION_REASON_LENGTH
                  }
                  onChange={(e) => setClosingText(e.target.value)}
                  className="w-full px-3 py-2 border border-ms-border rounded-md text-[12.5px] text-ms-text resize-none focus:outline-none focus:ring-2 focus:ring-ms-orange/40"
                />
                {closingError && (
                  <p className="text-[12.5px] text-rose-600" role="alert">
                    {closingError}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Btn
                    variant="accent"
                    size="sm"
                    disabled={pending !== null}
                    onClick={handleClose}
                  >
                    {closing === 'resolved' ? 'Resolve' : 'Reject'}
                  </Btn>
                  <Btn
                    variant="ghost"
                    size="sm"
                    disabled={pending !== null}
                    onClick={() => {
                      setClosing(null);
                      setClosingError(null);
                    }}
                  >
                    Cancel
                  </Btn>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
